import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./ProjectScreen.css";
import ProjectPreview from "../../components/ProjectPreview/ProjectPreview";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DummyPoster from "../../assets/DummyPoster.svg";
import { ReactComponent as PlusIcon } from "../../assets/plus-icon.svg";
import { ReactComponent as MenuIcon } from "../../assets/mobile/menu.svg";
import searchIcon from "../../assets/search.svg";
import uploadIcon from "../../assets/upload.svg";
import SortIcon from "../../assets/sort.svg";
import AppLogo from "../../assets/AppLogoBlue.svg";
import BackButton from "../../components/BackButton/BackButton";
import { useTranslation } from "react-i18next";
import AddProjectModal from "../../components/AddProjectModal/AddProjectModal";
import arrowDown from "../../assets/arrowDown.svg";
import Avatar from "../../components/Avatar/Avatar";
import { useAuth } from "../../context/AuthContext";
import {
  createVersion,
  deleteProject,
  deleteVersion,
  fetchProjectById,
  fetchProjectByIdForExplorer,
  fetchProjectByIdForAccess,
  fetchProjects,
  fetchProjectUsers,
  fetchSharedProjects,
  forceDeleteProject,
  forceDeleteVersion,
  getVersionUploadData,
  resetProjectsExplorer,
  setSharedProjectsStructure,
  updateSharedProjectChildren,
  updateProjectArchiveStatus,
  updateVersionArchiveStatus,
  fetchRecycleBin,
  clearRecycleBin,
  moveVersion,
} from "../../store/projectsSlice";
import {
  projectsSelector,
  userSelector,
  currentOrganizationSelector,
  organizationUsersSelector,
  currentOrganizationOwnerSelector,
  isMainLoadedSelector,
  organizationsListSelector,
  currentUploadingVideosSelector,
  sharedProjectsStructureSelector,
  recycleBinSelector,
  recycleBinLoadingSelector,
} from "../../store/selectors";
import AddVersionModal from "../../components/AddVersionModal/AddVersionModal";
import VersionPreview from "../../components/VersionPreview/VersionPreview";
import ProjectExplorer from "../../components/ProjectExplorer/ProjectExplorer";
import { OrbitProgress } from "react-loading-indicators";
import UsersPopup from "../../components/VideoPage/UsersPopup";
import EditProjectModal from "../../components/EditProjectModal/EditProjectModal";
import EditProjectPosterModal from "../../components/EditProjectPosterModal/EditProjectPosterModal";
import {
  setCurrentUploadingVideosInfo,
  setIsMainLoaded,
} from "../../store/appSlice";
import ProjectSharePopup from "./ProjectSharePopup";
import NotificationsPopup from "../../components/NotificationsPopup/NotificationsPopup";
import BacklogPopup from "../../components/VideoPage/BacklogPopup";
import EditVersionModal from "../../components/EditVersionModal/EditVersionModal";
import ProfilePopup from "../../components/ProfilePopup/ProfilePopup";
import SearchField from "../../components/SearchField/SearchField";
import useIsMobile from "../../hooks/useIsMobile";
import { updateTariffes } from "../../helpers/helpers";
import useOrganizationLimits from "../../hooks/useOrganizationLimits";
import toast from "react-hot-toast";
import {
  processShareToken,
  processShortShareToken,
} from "../../store/usersSlice";
import ReleaseNotesModal from "../../components/ReleaseNotesModal/ReleaseNotesModal";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import CloseButton from "../../components/CloseButton/CloseButton";
import { getInheritedProjectRole } from "../../helpers/permissions";
import useOrganizationRole from "../../hooks/useOrganizationRole";
import { useFileDrop } from "../../hooks/useFileDrop";
import { getUploadSpaceError } from "../../helpers/uploadLimits";
import DropTarget from "../../components/DropTarget/DropTarget";

function getQueryParam(search, param) {
  const params = new URLSearchParams(search);
  return params.get(param);
}

function ProjectScreen() {
  const dispatch = useDispatch();
  const { sharedToken, setShowReleaseNotes, showReleaseNotes } = useAuth();
  const isMobile = useIsMobile();
  const { projectId } = useParams();
  const location = useLocation();
  const project = useSelector((state) => state.projects.current);
  const [sharedProject, setSharedProject] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useTranslation();
  const projectsArray = useSelector(projectsSelector);
  const organizations = useSelector(organizationsListSelector);
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isPageLoading, setIsPageLoading] = useState(true);
  const sharedProjectsStructure = useSelector(sharedProjectsStructureSelector);
  const [projectToRename, setProjectToRename] = useState(null);
  const [projectToChangeCover, setProjectToChangeCover] = useState(null);
  const user = useSelector(userSelector);
  const currentOrganization = useSelector(currentOrganizationSelector);
  const [showOrganizationPopup, setShowOrganizationPopup] = useState(false);
  const [isOrgPopupOpen, setIsOrgPopupOpen] = useState(false);
  const [isBacklogPopupOpen, setBacklogPopupOpen] = useState(false);
  const limits = useOrganizationLimits();
  const [isAddSubprojectModalOpen, setIsAddSubprojectModalOpen] =
    useState(false);
  const [isShowReleaseNotes, setIsShowReleaseNotes] = useState(false);
  const [isAddVersionModalOpen, setIsAddVersionModalOpen] = useState(false);
  const [isUsersPopupOpen, setUsersPopupOpen] = useState(false);
  const [isVersionLoading, setIsVersionLoading] = useState(false);
  const [projectUsers, setProjectUsers] = useState([]);
  const [reuploadVersionId, setReuploadVersionId] = useState(null);
  const [sortOption, setSortOption] = useState("newest");
  const [showSortMenu, setShowSortMenu] = useState(false);
  const sortMenuRef = useRef(null);
  const recycleBinItems = useSelector(recycleBinSelector);
  const recycleBinLoading = useSelector(recycleBinLoadingSelector);
  const isMainLoaded = useSelector(isMainLoadedSelector);
  const [reuploadedVersionId, setReuploadedVersionId] = useState(null);
  const [currentMegabytes, setCurrentMegabytes] = useState(0);
  const [totalMegabytes, setTotalMegabytes] = useState(0);

  const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);
  let checkingTimeout = useRef();
  const currentUploadingVideosInfo = useSelector(
    currentUploadingVideosSelector
  );
  const [versionToRename, setVersionToRename] = useState(null);
  const [isViewer, setIsViewer] = useState(false);
  const isShared = !!sharedToken;
  const actualProject = project || sharedProject;
  const [isRootSharedProject, setIsRootSharedProject] = useState(true);
  const [myProjectRole, setMyProjectRole] = useState(null);
  const isCommenterRole = !!myProjectRole && myProjectRole.role === "COMMENTER";
  const isViewerRole = !!myProjectRole && myProjectRole.role === "VIEWER";
  const [isArchiveClearing, setIsArchiveClearing] = useState(false);
  const myOrgRole = useOrganizationRole();
  const hasOrgFullAccess =
    !!myOrgRole && (myOrgRole.role === "OWNER" || myOrgRole.role === "EDITOR");
  const isPassThroughFolder = !isShared && !hasOrgFullAccess && !myProjectRole;
  const canEdit =
    !isShared && !isCommenterRole && !isViewerRole && !showArchived;

  const { isDragActive: isPageDragActive, dropHandlers: pageDropHandlers } =
    useFileDrop({
      onFiles: (files) => uploadFiles(files),
      disabled: !canEdit || isPageLoading || isVersionLoading,
    });

  // isShared - человек незалогинен и прошел по шеринговой ссылке
  // isViewer - у человека доступ Viewer

  useEffect(() => {
    const redirectUrl = localStorage.getItem("redirectUrl");
    if (redirectUrl) {
      localStorage.removeItem("redirectUrl");
      navigate(redirectUrl);
    }
  }, []);

  useEffect(() => {
    if (!showArchived) return;
    if (!currentOrganization || !currentOrganization.id) return;
    dispatch(fetchRecycleBin({ organizationId: currentOrganization.id }));
  }, [showArchived, currentOrganization && currentOrganization.id]);

  useEffect(() => {
    if (!actualProject || !user) return;
    dispatch(fetchProjectUsers(projectId)).then((res) => {
      if (!res.error) {
        let usersArray = res.payload;
        setProjectUsers(usersArray);

        let myRole =
          usersArray.find((usr) => usr.userId === user.userId) || null;
        if (!myRole) {
          const ancestorRole = getInheritedProjectRole(
            user,
            (actualProject && actualProject.path) || []
          );
          if (ancestorRole) {
            myRole = {
              id: ancestorRole.id,
              role: ancestorRole.role,
              roleSourceProjectId: ancestorRole.projectId,
            };
          }
        }
        setMyProjectRole(myRole);
      }
    });
  }, [actualProject, user]);

  useEffect(() => {
    clearTimeout(checkingTimeout.current);
    setIsPageLoading(true);
    if (window.location.href.includes("role=viewer")) {
      setIsViewer(true);
    } else {
      setIsViewer(false);
    }

    if (isShared) {
      const processAction =
        sharedToken.length < 20 ? processShortShareToken : processShareToken;

      dispatch(processAction(sharedToken)).then((res) => {
        if (!res.error) {
          if (res.payload.id !== +projectId) {
            setIsRootSharedProject(false);

            dispatch(
              fetchProjectByIdForAccess({
                projectId,
                showArchived: false,
                isShared: true,
              })
            ).then((res2) => {
              if (!res2.error) {
                const newSharedProject = res2.payload;
                setSharedProject(newSharedProject);
                const newSharedProjectChildren = [
                  ...newSharedProject.subprojects,
                  ...newSharedProject.versions,
                ];
                let mappedNewSharedProjectChildren = {};
                for (let i = 0; i < newSharedProjectChildren.length; i++) {
                  let entity = newSharedProjectChildren[i];
                  mappedNewSharedProjectChildren[entity.id] = {
                    id: entity.id,
                    isFolder: !entity.version,
                    isRoot: false,
                    isShared: true,
                    isUnfolded: false,
                    title: entity.version || entity.title,
                  };
                }
                // const newSharedProjectsStructure = {
                //   [newSharedProject.id]: {
                //     children: mappedNewSharedProjectChildren,
                //     id: newSharedProject.id,
                //     isFolder: true,
                //     isRoot: true,
                //     isShared: true,
                //     isUnfolded: true,
                //     title: newSharedProject.title,
                //   },
                // };
                dispatch(
                  updateSharedProjectChildren(
                    res2.payload.id,
                    mappedNewSharedProjectChildren,
                    res2.payload.path
                  )
                );
                // dispatch(
                //   setSharedProjectsStructure(newSharedProjectsStructure)
                // );
                setIsPageLoading(false);
                dispatch(setIsMainLoaded(true));
              }
            });
          } else {
            setIsRootSharedProject(true);
            if (showReleaseNotes && res.payload.releaseNotes) {
              setIsShowReleaseNotes(true);
            }
            const newSharedProject = res.payload;
            setSharedProject(newSharedProject);
            const newSharedProjectChildren = [
              ...newSharedProject.subprojects,
              ...newSharedProject.versions,
            ];
            let mappedNewSharedProjectChildren = {};
            for (let i = 0; i < newSharedProjectChildren.length; i++) {
              let entity = newSharedProjectChildren[i];
              mappedNewSharedProjectChildren[entity.id] = {
                id: entity.id,
                isFolder: !entity.version,
                isRoot: false,
                isShared: true,
                isUnfolded: false,
                title: entity.version || entity.title,
              };
            }
            const newSharedProjectsStructure = {
              [newSharedProject.id]: {
                children: mappedNewSharedProjectChildren,
                id: newSharedProject.id,
                isFolder: true,
                isRoot: true,
                isShared: true,
                isUnfolded: true,
                title: newSharedProject.title,
              },
            };
            dispatch(setSharedProjectsStructure(newSharedProjectsStructure));
            setIsPageLoading(false);
            dispatch(setIsMainLoaded(true));
          }

          // dispatch(setSharedProjectsStructure(newSharedProjectsStructure));
          // setIsPageLoading(false);
          // dispatch(setIsMainLoaded(true));
        }
      });

      return;
    }

    const queryShared = getQueryParam(location.search, "shared");
    const currentUserRole = user.projectRoles.find(
      (role) => role.projectId === +projectId
    );
    const isLoggedShared =
      (currentUserRole &&
        (currentUserRole.role === "COMMENTER" ||
          currentUserRole.role === "VIEWER")) ||
      queryShared;

    if (isLoggedShared && showReleaseNotes) {
      setIsShowReleaseNotes(true);
    }

    if (!isMainLoaded) {
      dispatch(fetchProjects({ organizationId: currentOrganization.id })).then(
        (res) => {
          if (!res.error) {
            dispatch(
              fetchProjectById({ projectId, showArchived, isLoggedShared })
            ).then(async (res2) => {
              if (
                res2 &&
                res2.payload &&
                res2.payload.versions &&
                res2.payload.versions.length
              ) {
                const verIndex = res2.payload.versions.findIndex((ver) => {
                  const isEmpty =
                    ver.videoStatus === "empty" || !ver.videoStatus;
                  const isPending = ver.videoStatus === "pending";
                  const isFinal = ver.status === "FINAL";
                  return isPending || (isEmpty && isFinal);
                });
                if (verIndex > -1) {
                  updateCurrentProject();
                }
              }
              if (!res2.error) {
                const path = res2.payload.path;
                if (path.length > 1) {
                  for (let i = 0; i < path.length; i++) {
                    const pathEntry = path[i];
                    await dispatch(
                      fetchProjectByIdForExplorer({
                        projectId: pathEntry.id,
                        showArchived,
                        isLoggedShared,
                      })
                    );
                  }
                }
                setIsPageLoading(false);
                dispatch(setIsMainLoaded(true));

                const title = res2.payload.title;
                document.title = "Проекты • " + title;
              }
            });
            getUsersList();
          }
        }
      );
      dispatch(
        fetchSharedProjects({
          organizationId: currentOrganization.id,
          isDeleted: false,
        })
      );

      updateTariffes(user.organizations, dispatch);
    } else {
      dispatch(
        fetchProjectById({ projectId, showArchived, isLoggedShared })
      ).then((res) => {
        if (!res.error) {
          const fetchedProject = res.payload;
          const title = res.payload.title;
          document.title = "Проекты • " + title;
          setIsPageLoading(false);
          if (fetchedProject && fetchedProject.versions.length) {
            const verIndex = fetchedProject.versions.findIndex((ver) => {
              const isEmpty = ver.videoStatus === "empty" || !ver.videoStatus;
              const isPending = ver.videoStatus === "pending";
              const isFinal = ver.status === "FINAL";
              return isPending || (isEmpty && isFinal);
            });
            if (verIndex > -1) {
              updateCurrentProject();
            }
          }
        }
      });
      updateTariffes(user.organizations, dispatch);
      getUsersList();
    }

    return () => clearTimeout(checkingTimeout.current);
  }, [projectId, showArchived]);

  const updateCurrentProject = () => {
    clearTimeout(checkingTimeout.current);
    const queryShared = getQueryParam(location.search, "shared");
    const currentUserRole = user.projectRoles.find(
      (role) => role.projectId === +projectId
    );
    const isShared =
      (currentUserRole &&
        (currentUserRole.role === "COMMENTER" ||
          currentUserRole.role === "VIEWER")) ||
      queryShared;
    checkingTimeout.current = setTimeout(() => {
      dispatch(fetchProjectById({ projectId, showArchived, isShared })).then(
        (res) => {
          const updatedProj = res.payload;
          if (updatedProj && updatedProj.versions.length) {
            const verIndex = updatedProj.versions.findIndex((ver) => {
              const isEmpty = ver.videoStatus === "empty" || !ver.videoStatus;
              const isPending = ver.videoStatus === "pending";
              const isFinal = ver.status === "FINAL";
              const isInUploading = currentUploadingVideosInfo.find(
                (vidInfo) => vidInfo.data.id === ver.id
              );

              return !isInUploading && (isPending || (isEmpty && isFinal));
            });
            if (verIndex > -1) {
              updateCurrentProject();
            } else {
              updateTariffes(user.organizations, dispatch);
            }
          }
        }
      );
    }, 3000);
  };

  const handleCreateSubproject = async (projectData) => {
    const formData = new FormData();
    formData.append("title", projectData.title);
    formData.append("description", projectData.description);
    formData.append("organizationId", projectData.organizationId);
    formData.append("parentId", projectId);
    if (projectData.poster) {
      formData.append("poster", projectData.poster);
    }

    const result = await dispatch(createProject(formData));
    if (result.payload) {
      navigate(`/projects/${result.payload.id}`);
    }
  };

  // const handleUploadVersion = (file) => {
  //   if (!!reuploadVersionId) {
  //     handleReuploadVersion(file);
  //     return;
  //   }
  //   setIsVersionLoading(true);
  //   let versionData = {
  //     name: file.name,
  //     status: "IN_PROGRESS",
  //   };
  //   dispatch(createVersion({ projectId, versionData })).then((res) => {
  //     if (!res.error) {
  //       const versionId = res.payload.id;
  //       let uploads = {};
  //       const uploadedVideoInfo = {
  //         file,
  //         data: res.payload,
  //         isUploaded: false,
  //         isCompleted: false,
  //         parentProjectId: project.id,
  //       };

  //       dispatch(
  //         setCurrentUploadingVideosInfo([
  //           ...currentUploadingVideosInfo,
  //           uploadedVideoInfo,
  //         ])
  //       );
  //       fetchCurrentProjectById();
  //       setIsVersionLoading(false);
  //       setIsAddVersionModalOpen(false);
  //     } else {
  //       setIsVersionLoading(false);
  //     }
  //   });
  //};

  const uploadFiles = async (files, targetProjectId = project.id) => {
    const spaceError = getUploadSpaceError(limits, files);
    if (spaceError) {
      if (!limits) navigate("/pricing");
      else toast.error(spaceError);
      return false;
    }

    setIsVersionLoading(true);
    const started = [];
    for (const file of files) {
      const res = await dispatch(
        createVersion({
          projectId: targetProjectId,
          versionData: { name: file.name, status: "IN_PROGRESS" },
        })
      );
      if (res.error) {
        toast.error(`Не удалось начать загрузку «${file.name}»`);
        continue;
      }
      started.push({
        file,
        data: res.payload,
        isUploaded: false,
        isCompleted: false,
        parentProjectId: targetProjectId,
      });
    }
    setIsVersionLoading(false);
    if (!started.length) return false;

    dispatch((dispatch, getState) => {
      const current = getState().app.currentUploadingVideosInfo || [];
      dispatch(setCurrentUploadingVideosInfo([...current, ...started]));
    });

    if (targetProjectId === project.id) {
      fetchCurrentProjectById();
    } else {
      toast.success(
        started.length === 1
          ? "Видео загружается во вложенную папку"
          : `${started.length} видео загружаются во вложенную папку`
      );
    }
    return true;
  };

  const handleUploadVersion = async (file) => {
    if (!!reuploadVersionId) {
      handleReuploadVersion(file);
      return;
    }
    const ok = await uploadFiles([file]);
    if (ok) setIsAddVersionModalOpen(false);
  };

  const handleArchiveProject = (projectId, event) => {
    event.stopPropagation();
    if (window.confirm(t("projectScreen.confirmArchive"))) {
      dispatch(
        updateProjectArchiveStatus({ projectId, isArchived: true })
      ).then((res) => {
        if (!res.error) {
          toast.success("Проект перемещен в архив");
          if (project.id === projectId) {
            navigate("/home");
          } else {
            fetchCurrentProjectById();
          }
        }
      });
    }
  };

  const handleRestoreProject = (projectId, event) => {
    event.stopPropagation();
    if (
      window.confirm(
        t("Вы действительно хотите восстановить проект из архива?")
      )
    ) {
      dispatch(
        updateProjectArchiveStatus({ projectId, isArchived: false })
      ).then((res) => {
        if (!res.error) {
          toast.success("Проект восстановлен из архива");
          fetchCurrentProjectById();
        }
      });
    }
  };

  const handleArchiveVersion = (versionId, event) => {
    event.stopPropagation();
    if (window.confirm(t("projectScreen.confirmArchiveVersion"))) {
      dispatch(
        updateVersionArchiveStatus({ versionId, isArchived: true })
      ).then((res) => {
        if (!res.error) {
          toast.success("Версия перемещена в архив");
          fetchCurrentProjectById();
        }
      });
    }
  };

  const handleRestoreVersion = (versionId, event) => {
    event.stopPropagation();
    if (
      window.confirm("Вы действительно хотите восстановить версию из архива?")
    ) {
      dispatch(
        updateVersionArchiveStatus({ versionId, isArchived: false })
      ).then((res) => {
        if (!res.error) {
          toast.success("Версия восстановлена из архива");
          fetchCurrentProjectById();
        }
      });
    }
  };

  const handleDeleteProject = (projectId, event) => {
    event.stopPropagation();
    if (!showArchived) {
      if (window.confirm(t("projectScreen.confirmDelete"))) {
        dispatch(forceDeleteProject(projectId)).then((res) => {
          if (!res.error) {
            toast.success("Проект удален");
            if (project.id === projectId) {
              navigate("/home");
            } else {
              fetchCurrentProjectById();
            }
          }
        });
      }
      return;
    }
    if (window.confirm(t("projectScreen.confirmDelete"))) {
      dispatch(forceDeleteProject(projectId)).then((res) => {
        if (!res.error) {
          toast.success("Проект удален");
          if (project.id === projectId) {
            navigate("/home");
          } else {
            fetchCurrentProjectById();
          }
        }
      });
    }
  };

  const handleDeleteVersion = (versionId, event, isRecycleBin = false) => {
    event.stopPropagation();
    if (window.confirm(t("projectScreen.confirmDeleteVersion"))) {
      dispatch(forceDeleteVersion(versionId)).then((res) => {
        if (!res.error) {
          toast.success("Версия удалена");
          fetchCurrentProjectById();
        }
      });
    }
  };

  const filteredVersions =
    (actualProject &&
      actualProject.versions &&
      actualProject.versions.filter(
        (ver) => ver.deletionStatus !== "MARKED_FOR_DELETE"
      )) ||
    [];

  // Combine subprojects and versions for the list
  const combinedList = [
    ...filteredVersions,
    ...(actualProject?.subprojects || []),
  ];

  const filteredCombinedList = combinedList.filter(
    (item) =>
      (!!item.title &&
        item.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (!!item.version &&
        item.version.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleRenameProject = (event, projectId) => {
    event.preventDefault();
    event.stopPropagation();
    setIsSharePopupOpen(false);
    if (projectId === project.id) {
      setProjectToRename(project);
      return;
    }
    const existingProject = combinedList.find((p) => p.id === projectId);
    setProjectToRename(existingProject);
  };

  const handleChangeCover = (event, projectId) => {
    event.preventDefault();
    event.stopPropagation();
    setIsSharePopupOpen(false);
    if (projectId === project.id) {
      setProjectToChangeCover(project);
      return;
    }
    const existingProject = combinedList.find((p) => p.id === projectId);
    setProjectToChangeCover(existingProject);
  };

  const openVersionModalForReupload = (versionId) => {
    setIsAddVersionModalOpen(true);
    setReuploadVersionId(versionId);
  };

  const handleReuploadVersion = (file) => {
    setIsVersionLoading(true);
    dispatch(getVersionUploadData(reuploadVersionId)).then((res) => {
      if (!res.error) {
        let uploads = {};
        const uploadedVideoInfo = {
          file,
          data: res.payload,
          isCompleted: false,
          parentProjectId: project.id,
        };

        dispatch(
          setCurrentUploadingVideosInfo([
            ...currentUploadingVideosInfo,
            uploadedVideoInfo,
          ])
        );
        setIsVersionLoading(false);
        setIsAddVersionModalOpen(false);
      } else {
        setIsVersionLoading(false);
      }
    });
  };
  const handleRestoreFromRecycleBin = (versionId, event) => {
    if (event) event.stopPropagation();
    dispatch(updateVersionArchiveStatus({ versionId, isArchived: false })).then(
      (res) => {
        if (!res.error) {
          toast.success("Версия восстановлена");
          dispatch(fetchRecycleBin({ organizationId: currentOrganization.id }));
          if (project && project.id) fetchCurrentProjectById();
        } else {
          toast.error("Не удалось восстановить версию");
        }
      }
    );
  };

  const handleDeleteFromRecycleBin = (versionId, event) => {
    if (event) event.stopPropagation();
    const isConfirmed = window.confirm(
      "Вы действительно хотите удалить эту версию безвозвратно?"
    );
    if (!isConfirmed) return;

    dispatch(forceDeleteVersion(versionId)).then((res) => {
      if (!res.error) {
        toast.success("Версия удалена");
        dispatch(fetchRecycleBin({ organizationId: currentOrganization.id }));
      } else {
        toast.error("Не удалось удалить версию");
      }
    });
  };
  // Sorting logic
  const getSortedCombinedList = (list) => {
    let arr = [...list];
    switch (sortOption) {
      case "newest":
        arr.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
        break;
      case "oldest":
        arr.sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate));
        break;
      case "az":
        arr.sort((a, b) =>
          (a.title || a.version || "").localeCompare(
            b.title || b.version || "",
            "ru"
          )
        );
        break;
      case "za":
        arr.sort((a, b) =>
          (b.title || b.version || "").localeCompare(
            a.title || a.version || "",
            "ru"
          )
        );
        break;
      default:
        break;
    }
    return arr;
  };

  const sortedCombinedList = getSortedCombinedList(filteredCombinedList);
  const archiveList = showArchived
    ? recycleBinItems.filter((item) => {
        const query = searchQuery.toLowerCase();
        return (
          (item.version || "").toLowerCase().includes(query) ||
          (item.title || "").toLowerCase().includes(query) ||
          (item.projectTitle || "").toLowerCase().includes(query)
        );
      })
    : sortedCombinedList;
  // Adjust renderProject to handle both subprojects and versions
  const renderItem = (item, index) => {
    // If it's a version, it may not have a title, so use name or title
    const isVersion = !!item.version;

    if (isVersion) {
      let neededItem = item;
      const currentlyUploadingVideo =
        currentUploadingVideosInfo.length &&
        currentUploadingVideosInfo.find((vid) => vid.data.id === item.id);

      return (
        <VersionPreview
          canDrag={canEdit}
          projectId={actualProject?.id}
          isShared={isShared}
          versionInfo={neededItem}
          item={neededItem}
          currentlyUploadingVideo={currentlyUploadingVideo}
          vidStatus={neededItem.videoStatus}
          uploadStatus={currentlyUploadingVideo}
          project={project}
          key={neededItem.id}
          onClick={() => {
            if (!showArchived) {
              if (isShared && isViewer) {
                navigate(`/video/${neededItem.id}?shared=1&role=viewer`);
              } else if (isShared && !isViewer) {
                navigate(`/video/${neededItem.id}?shared=1`);
              } else {
                navigate(`/video/${neededItem.id}`);
              }
            }
          }}
          onDelete={handleDeleteVersion}
          onArchive={handleArchiveVersion}
          onRestore={handleRestoreVersion}
          reuploadVersion={openVersionModalForReupload}
          reuploadedVersionId={reuploadedVersionId}
          setReuploadedVersionId={setReuploadedVersionId}
          isArchive={showArchived}
          fetchCurrentProjectById={fetchCurrentProjectById}
        />
      );
    }
    return (
      <DropTarget
        key={item.id}
        disabled={!canEdit}
        onMoveVersion={(payload) => handleMoveVersion(payload, item)}
        onDropFiles={(files) => uploadFiles(files, item.id)}
      >
        {({ isOver, dropHandlers }) => (
          <ProjectPreview
            isShared={isShared}
            isCommenterRole={isCommenterRole}
            isViewerRole={isViewerRole}
            index={index}
            projectInfo={item}
            key={item.id}
            onClick={() => {
              if (!showArchived) {
                if (isShared && isViewer) {
                  navigate(`/projects/${item.id}?shared=1&role=viewer`);
                } else if (
                  (isShared && !isViewer) ||
                  isCommenterRole ||
                  isViewerRole
                ) {
                  navigate(`/projects/${item.id}?shared=1`);
                } else {
                  navigate(`/projects/${item.id}`);
                }
              }
            }}
            onDelete={handleDeleteProject}
            onArchive={handleArchiveProject}
            onRestore={handleRestoreProject}
            isArchive={showArchived}
            onRename={(e) => handleRenameProject(e, item.id)}
            onChangeCover={(e) => handleChangeCover(e, item.id)}
            fetchCurrentProjectById={fetchCurrentProjectById}
            dropHandlers={dropHandlers}
            isDropOver={isOver}
          />
        )}
      </DropTarget>
    );
  };

  const getPageTitle = () => {
    if ((!project && !sharedProject) || isPageLoading) return "Загрузка...";
    const neededProject = project || sharedProject;
    const projectPath = neededProject.path;
    let projectTitle = projectPath.reduce((res, current) => {
      res += `${current.title} • `;
      return res;
    }, "");
    return projectTitle.slice(0, projectTitle.length - 3);
  };

  const fetchCurrentProjectById = () => {
    dispatch(fetchProjectById({ projectId, showArchived })).then((res) => {
      if (!res.error) {
        updateTariffes(user.organizations, dispatch);
        setIsArchiveClearing(false);
      }
    });
  };

  const handleOrganizationChange = (organization) => {
    setIsPageLoading(true);
    dispatch({ type: "SET_CURRENT_ORGANIZATION", payload: organization });
    dispatch(
      fetchProjects({
        organizationId: organization.id,
        isDeleted: showArchived,
      })
    ).then((res) => {
      if (!res.error) {
        setIsPageLoading(false);
        dispatch(resetProjectsExplorer());
        navigate("/home");
      }
    });
    setShowOrganizationPopup(false);
  };

  const onPressAddOrganization = () => {
    setShowOrganizationPopup(false);
  };

  useEffect(() => {
    if (!showSortMenu) return;
    const handleClickOutside = (event) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target)) {
        setShowSortMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSortMenu]);

  const getUsersList = () => {
    dispatch(fetchProjectUsers(projectId)).then((res) => {
      if (!res.error) {
        let usersArray = res.payload;
        var unique = usersArray.filter(
          (value, index, array) =>
            array.findIndex((val) => val.email === value.email) === index
        );
        setProjectUsers(unique);
      }
    });
  };

  const handleMoveVersion = ({ versionId, fromProjectId }, target) => {
    if (!versionId || fromProjectId === target.id) return;
    dispatch(moveVersion({ versionId, targetProjectId: target.id })).then(
      (res) => {
        if (res.error) {
          const status = res.payload?.status;
          toast.error(
            status === 403
              ? "Нет прав загружать в эту папку"
              : "Не удалось переместить видео"
          );
          return;
        }
        toast.success(`Видео перемещено в «${target.title}»`);
        fetchCurrentProjectById();
        // Дерево слева: обновить обе папки
        dispatch(fetchProjectByIdForExplorer({ projectId: fromProjectId }));
        dispatch(fetchProjectByIdForExplorer({ projectId: target.id }));
      }
    );
  };

  const handleClearArchive = () => {
    if (!showArchived || isArchiveClearing) return;
    if (!currentOrganization || !currentOrganization.id) return;

    const isConfirmed = window.confirm(
      "Вы действительно хотите безвозвратно удалить все версии из архива организации? Это действие нельзя отменить."
    );
    if (!isConfirmed) return;

    setIsArchiveClearing(true);
    dispatch(
      clearRecycleBin({
        organizationId: currentOrganization.id,
        chosenVersionsArray: [],
      })
    )
      .then((res) => {
        if (!res.error) {
          toast.success("Архив очищен!");
          dispatch(fetchRecycleBin({ organizationId: currentOrganization.id }));
        } else {
          toast.error("Не удалось очистить архив");
        }
      })
      .finally(() => setIsArchiveClearing(false));
  };

  return (
    <div
      className={isMobile ? "projectScreen-App-mobile" : "projectScreen-App"}
    >
      <div
        className={
          isMobile ? "projectScreen-header-mobile" : "projectScreen-header"
        }
      >
        <div
          className={
            isMobile
              ? "projectScreen-header-left-mobile"
              : "projectScreen-header-left"
          }
        >
          {isMobile && (
            <div
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={{ display: "flex", marginTop: 7 }}
            >
              <MenuIcon color="white" />
            </div>
          )}
          <div
            onClick={() => navigate("/home")}
            className={
              isMobile
                ? "projectScreen-logoBlock-mobile"
                : "projectScreen-logoBlock"
            }
          >
            <img src={AppLogo} style={{ height: "100%" }} />
            {(isShared && !isViewer && (
              <button
                style={{
                  backgroundColor: "#4477ff",
                  border: "none",
                  padding: 10,
                  color: "white",
                  marginLeft: 20,
                  borderRadius: 80,
                  cursor: "pointer",
                }}
                onClick={() => {
                  window.location.href = window.location.origin;
                }}
              >
                Войти
              </button>
            )) ||
              null}

            {/* <span className="projectScreen-logo-text">
              {t("mainScreen.title")}
            </span> */}
          </div>
          {!isMobile && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {(!isShared || !isRootSharedProject) && (
                <BackButton
                  onClick={() => {
                    if (actualProject.path && actualProject.path.length) {
                      if (actualProject.path.length === 1) {
                        navigate("/home");
                      } else {
                        const parentId =
                          actualProject.path[actualProject.path.length - 2].id;
                        navigate(`/projects/${parentId}`);
                      }
                    }
                  }}
                />
              )}
              <span className="projectScreen-page-title">{getPageTitle()}</span>
            </div>
          )}
        </div>

        {/* <div className="language-switcher">
            <button
              className={`lang-button ${
                i18n.language === "ru" ? "active" : ""
              }`}
              onClick={() => changeLanguage("ru")}
            >
              RU
            </button>
            <button
              className={`lang-button ${
                i18n.language === "en" ? "active" : ""
              }`}
              onClick={() => changeLanguage("en")}
            >
              EN
            </button>
          </div> */}
        <div className="projectScreen-header-actions">
          {!isMobile && (
            <>
              <SearchField
                isShared={isShared}
                onValueChanged={(value) => setSearchQuery(value)}
              />
            </>
          )}
          {!isShared && (
            <>
              <NotificationsPopup />
              <ProfilePopup />
            </>
          )}
        </div>
      </div>
      <div className="projectScreen-content">
        {(!isMobile || isMenuOpen) && (
          <ProjectExplorer
            isShared={isShared}
            isViewer={isViewer}
            isMobile={isMobile}
            limits={limits}
            organizations={organizations}
            projectsArray={projectsArray}
            currentProject={actualProject}
            currentOrganization={currentOrganization}
            isOrgPopupOpen={isOrgPopupOpen}
            onOrganizationSelect={handleOrganizationChange}
            onAddOrganization={onPressAddOrganization}
            onOrganizationClick={() => setIsOrgPopupOpen(!isOrgPopupOpen)}
            projectUsers={projectUsers}
            isPageLoading={!isMainLoaded}
            showArchived={showArchived}
            canDrop={canEdit}
            onMoveVersion={handleMoveVersion}
            onDropFilesToFolder={uploadFiles}
          />
        )}
        <div
          className={`projectScreen-folders${
            isPageDragActive ? " projectScreen-folders--drop" : ""
          }`}
          {...pageDropHandlers}
        >
          {isPageDragActive && (
            <div className="projectScreen-drop-overlay">
              <img src={uploadIcon} alt="" width={40} height={40} />
              <span>Отпустите, чтобы загрузить в «{actualProject?.title}»</span>
            </div>
          )}
          {!isPageLoading && (
            <>
              <div className="projectScreen-folders-header">
                {/* {isPassThroughFolder && (
                  <div className="projectScreen-access-hint">
                    У вас нет доступа к этой папке — создавать и изменять
                    содержимое здесь нельзя. Доступны только вложенные папки, к
                    которым у вас есть права.
                  </div>
                )} */}
                <div style={{ position: "relative", maxWidth: "68%" }}>
                  <div
                    id="project-share-popup-block"
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      cursor: "pointer",
                      position: "relative",
                    }}
                    onClick={() => {
                      if (
                        isShared ||
                        isCommenterRole ||
                        isViewerRole ||
                        isPassThroughFolder ||
                        showArchived
                      )
                        return;

                      setIsSharePopupOpen((v) => !v);
                    }}
                  >
                    <div className="projectScreen-poster-thumb">
                      <img
                        src={
                          (actualProject && actualProject.posterUrl) ||
                          DummyPoster
                        }
                        style={{ height: "100%", width: "auto" }}
                      />
                    </div>
                    <span
                      className={
                        isMobile
                          ? "projectScreen-title-mobile"
                          : "projectScreen-title"
                      }
                    >
                      {actualProject &&
                        (showArchived ? `Архив` : actualProject.title)}
                    </span>
                    {!isShared &&
                      !isCommenterRole &&
                      !isViewerRole &&
                      !isPassThroughFolder &&
                      !showArchived && (
                        <img
                          id="arrow-down"
                          src={arrowDown}
                          alt="arrow"
                          style={{
                            width: 7,
                            height: 7,
                            transform: isSharePopupOpen
                              ? "rotate(180deg)"
                              : "none",
                          }}
                        />
                      )}
                  </div>
                  {isSharePopupOpen && (
                    <ProjectSharePopup
                      project={project}
                      onOpenTeamScreen={() => {
                        setIsSharePopupOpen(false);
                        setUsersPopupOpen(true);
                      }}
                      onClose={() => setIsSharePopupOpen(false)}
                      onOpenBacklog={() => setBacklogPopupOpen(true)}
                      onChangeCover={(e) => handleChangeCover(e, project.id)}
                      onRename={(e) => handleRenameProject(e, project.id)}
                      onDelete={handleDeleteProject}
                      onArchive={handleArchiveProject}
                      className={
                        isMobile
                          ? "project-share-popup-context-menu-absolute-mobile"
                          : "project-share-popup-context-menu-absolute"
                      }
                    />
                  )}
                  {isBacklogPopupOpen && (
                    <BacklogPopup
                      isOpen={isBacklogPopupOpen}
                      onClose={() => setBacklogPopupOpen(false)}
                    />
                  )}
                </div>

                <div className="main-projects-filter-buttons">
                  {!isShared &&
                    !isCommenterRole &&
                    !isViewerRole &&
                    !isPassThroughFolder && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          display: showArchived ? "none" : "flex",
                        }}
                      >
                        <button
                          className="projectScreen-add-folder-btn"
                          onClick={() => {
                            if (!limits) {
                              navigate("/pricing");
                              return;
                            }
                            const projectsLimit = limits.find(
                              (limit) =>
                                limit.attributeCode ===
                                "AVAILABLE_PROJECT_COUNT"
                            );
                            if (
                              projectsLimit &&
                              projectsLimit.usedValue >=
                                projectsLimit.maxValue &&
                              projectsLimit.maxValue !== -1
                            ) {
                              toast.error(
                                "Ваш текущий тариф не позволяет создать большее количество проектов."
                              );
                              return;
                            }
                            setIsAddSubprojectModalOpen(true);
                          }}
                        >
                          <PlusIcon
                            className="main-projects-add-btn-plus-icon"
                            style={{
                              transform: "scale(0.9)",
                            }}
                          />
                          {!isMobile && t("mainScreen.projects.addFolder")}
                        </button>
                        <button
                          className="projectScreen-add-version-btn"
                          onClick={() => {
                            setIsAddVersionModalOpen(true);
                            setReuploadVersionId(null);
                          }}
                        >
                          <img
                            src={uploadIcon}
                            alt="plus"
                            width={24}
                            height={24}
                          />
                          {!isMobile && t("mainScreen.projects.addVersion")}
                        </button>
                      </div>
                    )}
                </div>
                {!isMobile && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    {!isShared &&
                      !isCommenterRole &&
                      !isViewerRole &&
                      !isPassThroughFolder &&
                      !showArchived && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            className={`filter-btn ${
                              !showArchived ? "filter-btn--active" : ""
                            }`}
                            onClick={() => setShowArchived(false)}
                          >
                            {t("mainScreen.projects.filter.all")}
                          </button>

                          <button
                            className={`filter-btn ${
                              showArchived ? "filter-btn--active" : ""
                            }`}
                            style={{ marginLeft: 5 }}
                            onClick={() => setShowArchived(true)}
                          >
                            {t("mainScreen.projects.filter.archived")}
                          </button>
                        </div>
                      )}
                    {showArchived && (
                      <>
                        <PrimaryButton
                          onClick={handleClearArchive}
                          text={isArchiveClearing ? "Очищаем..." : "Очистить"}
                        />
                      </>
                    )}
                    <div>
                      <div
                        ref={sortMenuRef}
                        style={{
                          width: 32,
                          height: 32,
                          marginLeft: 10,
                          backgroundColor: "transparent",
                          boxShadow: "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative",
                        }}
                        onClick={() => setShowSortMenu((v) => !v)}
                      >
                        <img src={SortIcon} style={{ width: 28, height: 28 }} />
                        {showSortMenu && (
                          <div className="project-sort-menu">
                            <div
                              className={`project-sort-menu-item${
                                sortOption === "newest" ? " selected" : ""
                              }`}
                              onClick={(event) => {
                                event.stopPropagation();
                                setSortOption("newest");
                                setShowSortMenu(false);
                              }}
                            >
                              {sortOption === "newest" ? "✓ " : ""}Сначала новые
                            </div>
                            <div
                              className={`project-sort-menu-item${
                                sortOption === "oldest" ? " selected" : ""
                              }`}
                              onClick={(event) => {
                                event.stopPropagation();
                                setSortOption("oldest");
                                setShowSortMenu(false);
                              }}
                            >
                              {sortOption === "oldest" ? "✓ " : ""}Сначала
                              старые
                            </div>
                            <div
                              className={`project-sort-menu-item${
                                sortOption === "az" ? " selected" : ""
                              }`}
                              onClick={(event) => {
                                event.stopPropagation();
                                setSortOption("az");
                                setShowSortMenu(false);
                              }}
                            >
                              {sortOption === "az" ? "✓ " : ""}От А до Я
                            </div>
                            <div
                              className={`project-sort-menu-item${
                                sortOption === "za" ? " selected" : ""
                              }`}
                              onClick={(event) => {
                                event.stopPropagation();
                                setSortOption("za");
                                setShowSortMenu(false);
                              }}
                            >
                              {sortOption === "za" ? "✓ " : ""}От Я до А
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {showArchived && (
                      <>
                        <CloseButton
                          onClose={() => setShowArchived(false)}
                          styles={{
                            position: "relative",
                            top: 0,
                            right: 0,
                            marginRight: 3,
                            marginLeft: 5,
                          }}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>

              <div
                className={
                  isMobile ? "projectScreen-list-mobile" : "projectScreen-list"
                }
              >
                {showArchived && recycleBinLoading && (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      paddingTop: 24,
                    }}
                  >
                    <OrbitProgress style={{ fontSize: 8 }} color="#C1C7D4" />
                  </div>
                )}

                {!showArchived && archiveList.length === 0 && (
                  <div className="projectScreen-projects-container-dummy">
                    <div className="projectScreen-projects-container-dummy-title">
                      {t("mainScreen.projects.folderDummy.title")}
                    </div>
                    <span className="projectScreen-projects-container-dummy-subtitle">
                      {t("mainScreen.projects.folderDummy.subtitle")}
                    </span>
                  </div>
                )}

                {showArchived &&
                  !recycleBinLoading &&
                  archiveList.length === 0 && (
                    <div className="projectScreen-projects-container-dummy">
                      <div className="projectScreen-projects-container-dummy-title">
                        В архиве пока ничего нет
                      </div>
                      <span className="projectScreen-projects-container-dummy-subtitle">
                        Удалённые версии всей организации появятся здесь
                      </span>
                    </div>
                  )}

                {showArchived
                  ? archiveList.map((item) => (
                      <VersionPreview
                        key={`recycle-${item.id}`}
                        isShared={false}
                        versionInfo={item}
                        item={item}
                        currentlyUploadingVideo={false}
                        vidStatus={item.videoStatus}
                        project={item.project || project}
                        isArchive={true}
                        onRestore={handleRestoreFromRecycleBin}
                        onDelete={handleDeleteFromRecycleBin}
                        fetchCurrentProjectById={() => {}}
                      />
                    ))
                  : archiveList.map((item, index) => renderItem(item, index))}
              </div>
            </>
          )}
          {isPageLoading && (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <OrbitProgress color="#C1C7D4" />
            </div>
          )}
        </div>
      </div>
      {showOrganizationPopup && (
        <div className="modal-overlay">
          <div className="modal-content organization-popup">
            <h2>{t("mainScreen.organization.title")}</h2>
            <div className="organization-list">
              {organizations.map((org) => (
                <div key={org.id} className="organization-item">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="organization"
                      checked={currentOrganization.id === org.id}
                      onChange={() => handleOrganizationChange(org)}
                    />
                    <span title={org.title || org.name || org.email}>
                      {(org.title || org.name || org.email).length > 40
                        ? (org.title || org.name || org.email).slice(0, 40) +
                          "..."
                        : org.title || org.name || org.email}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            <button
              className="org-close-button"
              onClick={() => setShowOrganizationPopup(false)}
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      )}
      {projectToRename && (
        <EditProjectModal
          project={projectToRename}
          fetchCurrentProjectById={fetchCurrentProjectById}
          currentProject={project}
          onRenamed={() => {
            fetchCurrentProjectById();
          }}
          onClose={() => setProjectToRename(null)}
        />
      )}
      {versionToRename && (
        <EditVersionModal
          fetchCurrentProjectById={fetchCurrentProjectById}
          version={{ version: versionToRename, versionId: versionToRename.id }}
          status={versionToRename.videoStatus}
          currentProject={project}
          onClose={() => setVersionToRename(null)}
          onRenamed={() => {
            fetchCurrentProjectById();
          }}
        />
      )}
      {projectToChangeCover && (
        <EditProjectPosterModal
          project={projectToChangeCover}
          fetchCurrentProjectById={fetchCurrentProjectById}
          currentProject={project}
          onPosterUpdated={() => {
            fetchCurrentProjectById();
            setProjectToChangeCover(null);
          }}
          onClose={() => setProjectToChangeCover(null)}
        />
      )}
      {isAddSubprojectModalOpen && (
        <AddProjectModal
          parentId={projectId}
          onClose={() => setIsAddSubprojectModalOpen(false)}
          onSubmit={handleCreateSubproject}
        />
      )}
      {isShowReleaseNotes && (
        <ReleaseNotesModal
          isOpen={isShowReleaseNotes}
          onClose={() => {
            setIsShowReleaseNotes(false);
            setShowReleaseNotes(false);
          }}
          video={actualProject}
        />
      )}
      {isAddVersionModalOpen && (
        <AddVersionModal
          project={project}
          isVersionLoading={isVersionLoading}
          currentMegabytes={currentMegabytes}
          totalMegabytes={totalMegabytes}
          onClose={() => setIsAddVersionModalOpen(false)}
          onSubmit={handleUploadVersion}
        />
      )}
      {isUsersPopupOpen && (
        <UsersPopup
          projectInfo={project}
          organizationId={project?.organizationId || currentOrganization?.id}
          isOpen={isUsersPopupOpen}
          onClose={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setUsersPopupOpen(false);
          }}
          usersArray={projectUsers}
          updateUsersArray={getUsersList}
        />
      )}
      <div
        style={{
          position: "absolute",
          padding: "10px 8px",
          backgroundColor: "#3496FF",
          borderRadius: 8,
          bottom: 35,
          right: 35,
          color: "white",
          fontWeight: 500,
          fontSize: 14,
          cursor: "pointer",
        }}
        onClick={() => {
          navigate("/support");
        }}
      >
        Оставить отзыв
      </div>
    </div>
  );
}

export default ProjectScreen;
